import { useState, useCallback, useRef, useEffect } from 'react';

// ─── Helpers ────────────────────────────────────────────────────────────────

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function toMidnight(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function sameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatDate(d) {
  if (!d) return '';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ─── Styles (inline, no external deps) ───────────────────────────────────────

const styles = {
  wrapper: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    display: 'inline-flex',
    flexDirection: 'column',
    gap: 0,
  },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '9px 14px',
    border: '1.5px solid #d1d5db',
    borderRadius: 10,
    background: '#fff',
    cursor: 'pointer',
    fontSize: 14,
    color: '#374151',
    width: 280,
    transition: 'border-color .15s',
  },
  triggerIcon: { fontSize: 16, color: '#9ca3af', flexShrink: 0 },
  triggerText: { flex: 1, textAlign: 'left' },
  triggerChevron: { fontSize: 14, color: '#9ca3af' },
  popover: {
    marginTop: 6,
    background: '#fff',
    border: '1.5px solid #e5e7eb',
    borderRadius: 14,
    boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
    padding: '16px 16px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: 560,
    zIndex: 100,
  },
  calRow: { display: 'flex', gap: 20 },
  cal: { flex: 1, minWidth: 0 },
  calHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 10,
  },
  select: {
    flex: 1,
    fontSize: 13,
    fontWeight: 500,
    padding: '5px 6px',
    border: '1px solid #e5e7eb',
    borderRadius: 7,
    background: '#f9fafb',
    color: '#111827',
    cursor: 'pointer',
    appearance: 'auto',
  },
  navBtn: {
    width: 28,
    height: 28,
    border: '1px solid #e5e7eb',
    borderRadius: 7,
    background: 'transparent',
    color: '#6b7280',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 15,
    flexShrink: 0,
    lineHeight: 1,
  },
  dayGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '2px 1px',
  },
  dayLabel: {
    textAlign: 'center',
    fontSize: 11,
    fontWeight: 600,
    color: '#9ca3af',
    padding: '3px 0 5px',
    textTransform: 'uppercase',
    letterSpacing: '.05em',
  },
  divider: { height: 1, background: '#f3f4f6', margin: '2px 0' },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    paddingTop: 4,
  },
  rangeLabel: { fontSize: 13, color: '#6b7280', flex: 1 },
  rangeLabelStrong: { fontWeight: 600, color: '#111827' },
  btnClear: {
    padding: '4px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 7,
    background: 'transparent',
    color: '#6b7280',
    fontSize: 12,
    cursor: 'pointer',
  },
  btnApply: {
    padding: '4px 12px',
    border: 'none',
    borderRadius: 7,
    background: '#228093',
    color: '#fff',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnApplyDisabled: {
    padding: '4px 12px',
    border: 'none',
    borderRadius: 7,
    background: '#a8d5db',
    color: '#fff',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'not-allowed',
  },
};

// ─── Day Cell ─────────────────────────────────────────────────────────────────

function DayCell({ date, start, end, hovering, onClick, onMouseEnter }) {
  if (!date) return <div />;

  const today = toMidnight(new Date());
  const isToday = sameDay(date, today);
  const isStart = sameDay(date, start);
  const isEnd = sameDay(date, end);

  // effective range (confirmed or hover preview)
  const lo =
    start && end
      ? Math.min(start, end)
      : start && hovering
        ? Math.min(start, hovering)
        : null;
  const hi =
    start && end
      ? Math.max(start, end)
      : start && hovering
        ? Math.max(start, hovering)
        : null;
  const inRange = lo && hi && date > lo && date < hi;

  const isSelected = isStart || isEnd;
  const isSingle = isStart && isEnd; // same-day range

  // left/right rounding for range edges
  const rangeEdgeLeft = isStart && inRange && !isSingle;
  const rangeEdgeRight = isEnd && inRange && !isSingle;

  const baseCell = {
    textAlign: 'center',
    fontSize: 13,
    lineHeight: '30px',
    height: 30,
    cursor: 'pointer',
    userSelect: 'none',
    position: 'relative',
    borderRadius: 0,
    background: 'transparent',
    color: '#374151',
    fontWeight: isToday ? 600 : 400,
  };

  if (isSelected && !inRange) {
    return (
      <div
        style={{
          ...baseCell,
          background: '#228093',
          color: '#fff',
          borderRadius: 7,
        }}
        onClick={() => onClick(date)}
        onMouseEnter={() => onMouseEnter(date)}
      >
        {date.getDate()}
      </div>
    );
  }

  if (rangeEdgeLeft) {
    return (
      <div
        style={{ ...baseCell, background: '#d0eef1' }}
        onClick={() => onClick(date)}
        onMouseEnter={() => onMouseEnter(date)}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: '#228093',
            color: '#fff',
            borderRadius: '7px 0 0 7px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {date.getDate()}
        </div>
      </div>
    );
  }

  if (rangeEdgeRight) {
    return (
      <div
        style={{ ...baseCell, background: '#d0eef1' }}
        onClick={() => onClick(date)}
        onMouseEnter={() => onMouseEnter(date)}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: '#228093',
            color: '#fff',
            borderRadius: '0 7px 7px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {date.getDate()}
        </div>
      </div>
    );
  }

  if (inRange) {
    return (
      <div
        style={{ ...baseCell, background: '#d0eef1', color: '#0d5460' }}
        onClick={() => onClick(date)}
        onMouseEnter={() => onMouseEnter(date)}
      >
        {date.getDate()}
      </div>
    );
  }

  return (
    <div
      style={{
        ...baseCell,
        color: isToday ? '#228093' : '#374151',
        borderRadius: 7,
      }}
      onClick={() => onClick(date)}
      onMouseEnter={() => onMouseEnter(date)}
      onMouseLeave={() => onMouseEnter(null)}
    >
      {date.getDate()}
    </div>
  );
}

// ─── Single Calendar ──────────────────────────────────────────────────────────

function Calendar({
  year,
  month,
  start,
  end,
  hovering,
  onDayClick,
  onDayHover,
  onMonthChange,
  onYearChange,
  onPrev,
  onNext,
}) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const minYear = new Date().getFullYear() - 20;
  const maxYear = new Date().getFullYear() + 10;
  const yearOptions = Array.from(
    { length: maxYear - minYear + 1 },
    (_, i) => minYear + i,
  );

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  return (
    <div style={styles.cal}>
      <div style={styles.calHeader}>
        <button
          style={styles.navBtn}
          onClick={onPrev}
          aria-label="Previous month"
        >
          ‹
        </button>
        <select
          style={styles.select}
          value={month}
          onChange={(e) => onMonthChange(parseInt(e.target.value))}
        >
          {MONTHS.map((m, i) => (
            <option key={i} value={i}>
              {m}
            </option>
          ))}
        </select>
        <select
          style={styles.select}
          value={year}
          onChange={(e) => onYearChange(parseInt(e.target.value))}
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <button style={styles.navBtn} onClick={onNext} aria-label="Next month">
          ›
        </button>
      </div>

      <div style={styles.dayGrid}>
        {DAY_LABELS.map((d) => (
          <div key={d} style={styles.dayLabel}>
            {d}
          </div>
        ))}
        {cells.map((date, i) => (
          <DayCell
            key={i}
            date={date}
            start={start}
            end={end}
            hovering={hovering}
            onClick={onDayClick}
            onMouseEnter={onDayHover}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * DateRangePicker
 *
 * Props:
 *  - defaultStartDate {Date|null}  initial start
 *  - defaultEndDate   {Date|null}  initial end
 *  - minDate          {Date|null}  earliest selectable date
 *  - maxDate          {Date|null}  latest selectable date
 *  - onChange         {(start: Date, end: Date) => void}  fired on Apply
 *  - placeholder      {string}     trigger button text when nothing selected
 */
export default function DateRangePicker({
  defaultStartDate = null,
  defaultEndDate = null,
  onChange,
  placeholder = 'Select date',
}) {
  const today = toMidnight(new Date());
  const wrapperRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(
    defaultStartDate ? toMidnight(defaultStartDate) : null,
  );
  const [end, setEnd] = useState(
    defaultEndDate ? toMidnight(defaultEndDate) : null,
  );
  const [hovering, setHovering] = useState(null);

  // draft state while popover is open
  const [draftStart, setDraftStart] = useState(start);
  const [draftEnd, setDraftEnd] = useState(end);

  const initViews = () => {
    const base = draftStart || today;
    const m0 = base.getMonth();
    const y0 = base.getFullYear();
    const m1 = m0 === 11 ? 0 : m0 + 1;
    const y1 = m0 === 11 ? y0 + 1 : y0;
    return [
      { y: y0, m: m0 },
      { y: y1, m: m1 },
    ];
  };

  const [views, setViews] = useState(initViews);

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  const openPicker = () => {
    setDraftStart(start);
    setDraftEnd(end);
    setViews(initViews());
    setHovering(null);
    setOpen(true);
  };

  const handleDayClick = useCallback(
    (date) => {
      const d = toMidnight(date);
      if (!draftStart || (draftStart && draftEnd)) {
        // start fresh
        setDraftStart(d);
        setDraftEnd(null);
      } else {
        // second click — end date (can equal start)
        setDraftEnd(d);
        setHovering(null);
      }
    },
    [draftStart, draftEnd],
  );

  const handleDayHover = useCallback(
    (date) => {
      if (draftStart && !draftEnd) {
        setHovering(date ? toMidnight(date) : null);
      }
    },
    [draftStart, draftEnd],
  );

  const handleApply = () => {
    if (!draftStart) return;
    const s = draftStart;
    const e = draftEnd || draftStart; // same day if no end chosen
    const lo = s <= e ? s : e;
    const hi = s <= e ? e : s;
    setStart(lo);
    setEnd(hi);
    onChange && onChange(lo, hi);
    setOpen(false);
  };

  const handleClear = () => {
    setDraftStart(null);
    setDraftEnd(null);
    setHovering(null);
  };

  const handleClose = () => setOpen(false);

  // navigation helpers
  const navigate = (idx, delta) => {
    setViews((prev) => {
      const next = [...prev];
      let { y, m } = next[idx];
      m += delta;
      if (m < 0) {
        m = 11;
        y--;
      }
      if (m > 11) {
        m = 0;
        y++;
      }
      next[idx] = { y, m };
      return next;
    });
  };

  // Trigger label
  const triggerLabel = (() => {
    if (!start && !end) return null;
    if (start && end && sameDay(start, end)) return formatDate(start);
    if (start && end) return `${formatDate(start)} → ${formatDate(end)}`;
    if (start) return formatDate(start);
    return null;
  })();

  // Action row label
  const actionLabel = (() => {
    if (!draftStart) return 'Select date';
    if (draftStart && !draftEnd)
      return (
        <span>
          <span style={styles.rangeLabelStrong}>{formatDate(draftStart)}</span>
          {' → pick end date'}
        </span>
      );
    const lo = draftStart <= draftEnd ? draftStart : draftEnd;
    const hi = draftStart <= draftEnd ? draftEnd : draftStart;
    if (sameDay(lo, hi))
      return <span style={styles.rangeLabelStrong}>{formatDate(lo)}</span>;
    return (
      <span>
        <span style={styles.rangeLabelStrong}>{formatDate(lo)}</span>
        {' → '}
        <span style={styles.rangeLabelStrong}>{formatDate(hi)}</span>
      </span>
    );
  })();

  const canApply = !!draftStart;

  return (
    <div ref={wrapperRef} style={styles.wrapper}>
      {/* Trigger */}
      <button
        style={{
          ...styles.trigger,
          // borderColor: open ? '#228093' : '#d1d5db',
          marginTop: '5px',
          border: '1px solid #228093',
        }}
        onClick={() => (open ? handleClose() : openPicker())}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span style={styles.triggerIcon}>📅</span>
        <span
          style={{
            ...styles.triggerText,
            color: triggerLabel ? '#111827' : '#9ca3af',
          }}
        >
          {triggerLabel || placeholder}
        </span>
        <span style={styles.triggerChevron}>{open ? '▲' : '▼'}</span>
      </button>

      {/* Popover */}
      {open && (
        <div
          style={styles.popover}
          role="dialog"
          aria-label="Date range picker"
        >
          <div style={styles.calRow}>
            <Calendar
              year={views[0].y}
              month={views[0].m}
              start={draftStart}
              end={draftEnd}
              hovering={hovering}
              onDayClick={handleDayClick}
              onDayHover={handleDayHover}
              onMonthChange={(m) => setViews((v) => [{ ...v[0], m }, v[1]])}
              onYearChange={(y) => setViews((v) => [{ ...v[0], y }, v[1]])}
              onPrev={() => navigate(0, -1)}
              onNext={() => navigate(0, +1)}
            />
            <div style={{ width: 1, background: '#f3f4f6', flexShrink: 0 }} />
            <Calendar
              year={views[1].y}
              month={views[1].m}
              start={draftStart}
              end={draftEnd}
              hovering={hovering}
              onDayClick={handleDayClick}
              onDayHover={handleDayHover}
              onMonthChange={(m) => setViews((v) => [v[0], { ...v[1], m }])}
              onYearChange={(y) => setViews((v) => [v[0], { ...v[1], y }])}
              onPrev={() => navigate(1, -1)}
              onNext={() => navigate(1, +1)}
            />
          </div>

          <div style={styles.divider} />

          <div style={styles.actions}>
            <div style={styles.rangeLabel}>{actionLabel}</div>
            <button style={styles.btnClear} onClick={handleClear}>
              Clear
            </button>
            <button
              style={canApply ? styles.btnApply : styles.btnApplyDisabled}
              onClick={handleApply}
              disabled={!canApply}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Demo usage (remove in production) ───────────────────────────────────────
// import DateRangePicker from "./DateRangePicker";
//
// function App() {
//   return (
//     <DateRangePicker
//       placeholder="Select date"
//       onChange={(start, end) => console.log("Range:", start, end)}
//     />
//   );
// }
